import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ThumbsUp, MessageCircle } from "lucide-react";
import { UserContext } from "../../../UserContext";
import { fetchAllBlogs, likeBlog } from "../../../services/blogService";
import { getBlogId } from "../../../utils/blogUtils";

const fallbackURL =
  "https://media.istockphoto.com/id/2174551157/photo/cyber-security-data-protection-business-technology-privacy-concept.jpg?s=2048x2048&w=is&k=20&c=sCjAoM5vMvI7PXGFaTaOnRr1AppyOWmxPHkckruBq_A=";

const UserBlogList = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [allTags, setAllTags] = useState([]);
  const { user } = useContext(UserContext);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);

    const loadBlogs = async () => {
      try {
        const data = await fetchAllBlogs();
        const sortedBlogs = (data ?? []).sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setBlogs(sortedBlogs);

        const tagCounts = {};
        sortedBlogs.forEach((blog) => {
          if (!blog.blog_tags) return;
          blog.blog_tags.split(",").forEach((tag) => {
            const cleanTag = tag.trim();
            if (!cleanTag) return;
            tagCounts[cleanTag] = (tagCounts[cleanTag] || 0) + 1;
          });
        });

        setAllTags(
          Object.entries(tagCounts).map(([name, count]) => ({
            name,
            count,
          }))
        );
      } catch (err) {
        setError(err.message);
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    loadBlogs();
  }, []);

  const handleLike = async (blogId, index, e) => {
    e.stopPropagation();

    if (!user?.user_id) {
      navigate(
        `/signUp?redirect=${encodeURIComponent(location.pathname + location.search)}`
      );
      return;
    }

    const likedKey = `liked-${blogId}`;
    if (localStorage.getItem(likedKey) === "true") return;

    const previousBlogs = [...blogs];
    const updatedBlogs = [...blogs];
    updatedBlogs[index] = {
      ...updatedBlogs[index],
      like_count: (updatedBlogs[index].like_count || 0) + 1,
    };
    setBlogs(updatedBlogs);
    localStorage.setItem(likedKey, "true");

    try {
      await likeBlog(blogId, user.access_token);
    } catch (err) {
      console.error("Error liking blog:", err);
      setBlogs(previousBlogs);
      localStorage.removeItem(likedKey);
    }
  };

  const filteredBlogs = selectedTag
    ? blogs.filter((blog) =>
        blog.blog_tags
          ?.split(",")
          .map((tag) => tag.trim())
          .includes(selectedTag)
      )
    : blogs;

  if (loading) return <p>Loading blogs...</p>;
  if (error) return <p className="text-red-600">{error}</p>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <div className="mb-6 flex flex-wrap gap-3">
        <button
          onClick={() => setSelectedTag("")}
          className={`px-4 py-1 rounded-full text-sm ${
            selectedTag === "" ? "bg-[#54B0AF] text-white" : "bg-gray-200"
          }`}
        >
          All
        </button>
        {allTags.map((tag) => (
          <button
            key={tag.name}
            onClick={() => setSelectedTag(tag.name)}
            className={`px-4 py-1 rounded-full text-sm ${
              selectedTag === tag.name
                ? "bg-[#54B0AF] text-white"
                : "bg-gray-200"
            }`}
          >
            {tag.name} ({tag.count})
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
        {filteredBlogs.length > 0 ? (
          filteredBlogs.map((blog) => {
            const liked =
              localStorage.getItem(`liked-${getBlogId(blog)}`) === "true";
            const blogIndex = blogs.findIndex(
              (entry) => getBlogId(entry) === getBlogId(blog)
            );

            return (
              <div
                key={getBlogId(blog)}
                className="bg-white rounded-2xl shadow hover:shadow-lg transition duration-300 overflow-hidden cursor-pointer flex flex-col h-full"
                onClick={() =>
                  navigate(`/blogs/${getBlogId(blog)}`, { state: { blog } })
                }
              >
                <img
                  src={blog.image_url || fallbackURL}
                  alt="Blog"
                  className="w-full h-48 object-cover"
                />
                <div className="p-4">
                  <div className="flex justify-between text-xs text-gray-500 mt-2">
                    <div className="flex items-center mb-2">
                      <img
                        src={`https://ui-avatars.com/api/?name=${blog.username}`}
                        alt={blog.username}
                        className="w-7 h-7 rounded-full mr-2"
                      />
                      <span className="text-sm text-black font-medium">
                        {blog.username}
                      </span>
                    </div>
                    <span className="mt-1">
                      {new Date(blog.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold mb-1">{blog.title}</h3>
                  <p
                    className="text-sm text-gray-600 mb-3 prose max-w-none"
                    dangerouslySetInnerHTML={{
                      __html:
                        blog.description.slice(0, 300) +
                        (blog.description.length > 300 ? "..." : ""),
                    }}
                  />

                  {blog.blog_tags && (
                    <div className="mt-6 flex flex-wrap gap-2">
                      {blog.blog_tags.split(",").map((tag, index) => {
                        const trimmedTag = tag.trim();
                        return (
                          <button
                            key={`${getBlogId(blog)}-${index}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTag(trimmedTag);
                            }}
                            className={`px-3 py-1 rounded-full text-sm border ${
                              selectedTag === trimmedTag
                                ? "bg-[#54B0AF] text-white"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            {trimmedTag}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <div className="flex items-center gap-4 mt-3 text-gray-600">
                    <button
                      className={`flex items-center gap-1 ${
                        liked ? "text-blue-600" : "hover:text-blue-600"
                      }`}
                      onClick={(e) => handleLike(getBlogId(blog), blogIndex, e)}
                    >
                      <ThumbsUp className="w-4 h-4" />
                      {blog.like_count || 0}
                    </button>
                    <div className="flex items-center gap-1">
                      <MessageCircle className="w-4 h-4" />
                      <p>{blog.comment_count || 0}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <p>No blogs available.</p>
        )}
      </div>
    </div>
  );
};

export default UserBlogList;
